import React, { useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Box, Typography } from '@material-ui/core';
import CalendarIcon from '../assets/calendarIcon';
import { useLoanStore } from '@roc/feature-loans';
import { observer } from 'mobx-react';
import { PopupModal } from 'react-calendly';
// TODO: maybe we could move this to a shared location?
// import { loanStatus, loanType } from "../../../../../../apps/roc-mobile/src/enums/loans";

const useStyles = makeStyles(theme => ({
  blueBg: {
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '14px',
    boxShadow:
      '0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    height: 'auto',
    '@media (min-width: 992px)': {
      width: '420px',
    },
  },
  threeBoxBlueBg: {
    '@media (min-width: 992px)': {
      width: '630px',
    },
  },
  box: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50%',
    padding: '8px 16px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    wordBreak: 'break-word',
  },
  contactContainer: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    width: '100%',
    overflow: 'hidden',
    maxWidth: '134px',
  },
  leftBox: {
    justifyContent: 'flex-end',
    borderRight: '2px solid white',
  },
  middleBox: {
    width: '33.33%',
    borderRight: '2px solid white',
  },
  rightBox: {
    justifyContent: 'flex-start',
  },
  thirdBox: {
    width: '33.33%',
  },
  singleBox: {
    width: '100%',
  },
  singleBoxBlueBg: {
    width: 'auto',
    borderRadius: '16px',
    '@media (min-width: 992px)': {
      minWidth: '239px',
    },
  },
  // TODO: I haven't found a way to nest it into singleBox
  singleBoxName: {
    color: '#E4E8EB',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: '0.25px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
    lineHeight: '143%',
    textAlign: 'center',
  },
  singleBoxRole: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 700,
    letterSpacing: '0.15px',
    whiteSpace: 'nowrap',
    lineHeight: '175%',
    '@media (max-width: 992px)': {
      width: '118px',
    },
  },
  icon: {
    marginRight: '24px',
  },
  role: {
    color: '#E4E8EB',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 400,
    letterSpacing: '0.25px',
    whiteSpace: 'normal',
    lineHeight: '1.3',
    '@media (max-width: 992px)': {
      width: '118px',
    },
  },
  name: {
    color: '#fff',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 700,
    letterSpacing: '0.15px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
  },
  clickableBox: {
    cursor: 'pointer',
  },
}));

export const ContactsCard = observer(() => {
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState('');

  const { loanStore } = useLoanStore();
  const { loanDetails } = loanStore;

  const loanId = loanStore.loanDetails?.loanId;
  const calendlyPreferences = loanStore.calendlyPreferences;
  const prefill = calendlyPreferences.calendlyPrefill;
  const showCalendly = calendlyPreferences.showCalendly;
  const loanUnderwriter = loanDetails?.loanUnderwriter;
  const loanCoordinator =
    loanDetails?.primaryCscContact ?? loanDetails?.loanCoordinator;
  const internalCloser = loanDetails?.internalCloser;
  const internalCloserId = loanDetails?.internalCloserId;
  const drawsReviewer = loanDetails?.drawsReviewSpecialist ?? '';
  const drawsReviewerId = 123; // TODO: no ID is returned by the API
  const displayDrawsReviewer =
    loanDetails?.status === 'FUNDED' &&
    loanDetails?.loanType === 'Residential Bridge';
  const classes = useStyles();

  const handleContactClick = (type: string) => {
    switch (type) {
      case 'drawsReviewer':
        setCalendlyUrl(
          calendlyPreferences.calendlyUsers[drawsReviewer.userId]?.calendlyUrl
        );
        break;
      case 'loanCoordinator':
        setCalendlyUrl(
          calendlyPreferences.calendlyUsers[loanCoordinator?.userId]
            ?.calendlyUrl
        );
        break;
      case 'internalCloser':
        setCalendlyUrl(
          calendlyPreferences.calendlyUsers[internalCloserId]?.calendlyUrl
        );
        break;
      case 'underwritter':
        setCalendlyUrl(
          calendlyPreferences.calendlyUsers[loanUnderwriter?.userId]
            ?.calendlyUrl
        );
        break;
      default:
        return;
    }
    setIsCalendlyModalOpen(true);
  };

  useEffect(() => {
    loanStore.getCalendlyPreferences(loanId);
  }, []);

  return (
    <>
      <Box
        className={`${classes.blueBg} ${
          displayDrawsReviewer
            ? classes.singleBoxBlueBg
            : internalCloser
            ? classes.threeBoxBlueBg
            : ''
        }`}
      >
        {displayDrawsReviewer ? (
          <Box
            className={`${classes.box} ${classes.singleBox} ${
              calendlyPreferences?.calendlyUsers[drawsReviewer.userId]
                ?.calendlyUrl
                ? classes.clickableBox
                : ''
            }`}
            onClick={() => handleContactClick('drawsReviewer')}
          >
            <Box className={classes.icon}>
              <CalendarIcon />
            </Box>
            <Box className={classes.contactContainer}>
              <Typography className={classes.singleBoxName}>
                {drawsReviewer?.name}
              </Typography>
              <Typography className={classes.singleBoxRole}>
                Draws Reviewer
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              className={`${classes.box} ${classes.leftBox} ${
                internalCloser ? classes.thirdBox : ''
              } ${
                calendlyPreferences?.calendlyUsers[loanCoordinator?.userId]
                  ?.calendlyUrl
                  ? classes.clickableBox
                  : ''
              }`}
              onClick={() => handleContactClick('loanCoordinator')}
            >
              <Box className={classes.icon}>
                <CalendarIcon />
              </Box>
              <Box className={classes.contactContainer}>
                <Typography className={classes.name}>
                  {loanCoordinator?.name || 'N/A'}
                </Typography>
                <Typography className={classes.role}>
                  Client Success Coordinator
                </Typography>
              </Box>
            </Box>
            {internalCloser && (
              <Box
                className={`${classes.box} ${classes.middleBox} ${
                  calendlyPreferences?.calendlyUsers[internalCloserId]
                    ?.calendlyUrl
                    ? classes.clickableBox
                    : ''
                }`}
                onClick={() => handleContactClick('internalCloser')}
              >
                <Box className={classes.icon}>
                  <CalendarIcon />
                </Box>
                <Box className={classes.contactContainer}>
                  <Typography className={classes.name}>
                    {internalCloser}
                  </Typography>
                  <Typography className={classes.role}>
                    Internal Closer
                  </Typography>
                </Box>
              </Box>
            )}
            <Box
              className={`${classes.box} ${classes.rightBox} ${
                internalCloser ? classes.thirdBox : ''
              } ${
                calendlyPreferences?.calendlyUsers[loanUnderwriter?.userId]
                  ?.calendlyUrl
                  ? classes.clickableBox
                  : ''
              }`}
              onClick={() => handleContactClick('underwritter')}
            >
              <Box className={classes.icon}>
                <CalendarIcon />
              </Box>
              <Box className={classes.contactContainer}>
                <Typography className={classes.name}>
                  {loanUnderwriter?.name || 'N/A'}
                </Typography>
                <Typography className={classes.role}>Underwriter</Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {calendlyUrl && (
        <CalendlyWidget
          isCalendlyModalOpen={isCalendlyModalOpen}
          setIsCalendlyModalOpen={setIsCalendlyModalOpen}
          calendlyUrl={calendlyUrl}
          prefill={prefill}
          showCalendly={showCalendly}
        />
      )}
    </>
  );
});

const CalendlyWidget = observer(
  ({
    isCalendlyModalOpen,
    setIsCalendlyModalOpen,
    calendlyUrl,
    prefill,
    showCalendly,
  }) => {
    return (
      <>
        {showCalendly && calendlyUrl && (
          <PopupModal
            url={`https://calendly.com/${calendlyUrl}`}
            prefill={prefill}
            onModalClose={() => setIsCalendlyModalOpen(false)}
            open={isCalendlyModalOpen}
            rootElement={document.getElementById('root')}
          />
        )}
      </>
    );
  }
);
